import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Save, FolderOpen } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useCourseStore } from "../../store/courseStore";

interface CategoriesSectionProps {
  courseId?: string;
  initialData: any;
}

export default function CategoriesSection({
  courseId,
  initialData,
}: CategoriesSectionProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { categories, fetchCategories } = useCourseStore();
  const [selectedCategory, setSelectedCategory] = useState(
    initialData?.category_id || "",
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const checkForChanges = useCallback(() => {
    const hasChanges = selectedCategory !== (initialData?.category_id || "");
    setHasUnsavedChanges(hasChanges);
  }, [selectedCategory, initialData]);

  useEffect(() => {
    checkForChanges();
  }, [selectedCategory, checkForChanges]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const { error } = await supabase
        .from("courses")
        .update({ category_id: selectedCategory || null })
        .eq("id", courseId);

      if (error) throw error;

      // Update initialData reference after successful save
      Object.assign(initialData, { category_id: selectedCategory });
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error("Error saving category:", err);
      setError(err instanceof Error ? err.message : "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-gray-900">Category</h2>
          <button
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium ${
              hasUnsavedChanges
                ? "text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
                : "text-gray-500 bg-gray-100"
            } disabled:opacity-50 transition-colors duration-200`}
          >
            <Save className="h-5 w-5 mr-2" />
            {saving ? "Saving..." : "Save Category"}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Course Category
          </label>
          <div className="mt-1">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
            >
              <option value="">Select a category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Categorizing your course helps students find it more easily.
          </p>
        </div>

        {/* Quick category creation */}
        <div className="mt-6 border-t border-gray-200 pt-6">
          <h3 className="text-sm font-medium text-gray-900">
            Need a new category?
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Visit the{" "}
            <Link
              to="/admin/course-categories"
              className="text-[#F98B3D] hover:text-[#e07a2c]"
            >
              Categories Management
            </Link>{" "}
            page to create or edit categories.
          </p>
        </div>
      </div>
    </div>
  );
}
